import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import {
  buildEffectiveOperatingIntervalsFmt,
  findNextOpeningFmt,
} from '~/client/src/shared/utils/intervalFormatters'

/**
 * Whether a location is open, by the two things that can shut it.
 *
 * The single seam for the Opened/Closed badge. It used to be answered four ways:
 * the jobsite homepage asked about closures AND hours, while the resource
 * controls table, its status sort and the map card each asked about closures
 * alone - so a resource four hours before it opens still read "Opened" beside
 * the very hours saying otherwise.
 *
 * Hours come through buildEffectiveOperatingIntervalsFmt, so the answer carries
 * the same rules the booking form applies: a dated window governs its own date,
 * below it the weekday's own, below that an every-day one, and the project's
 * week where the resource states nothing recurring of its own.
 */
interface ILocationOpenSource {
  id: string
  operatingIntervals?: IMsDateInterval[]
}

interface ILocationOpenContext {
  projectDateStore: ProjectDateStore
  closuresStore: ClosuresStore
  /**
   * The range the surface is showing, where it has one. The map card is the only
   * caller that does; the controls table and the homepage speak for right now.
   */
  selectedStartDate?: Date
  selectedEndDate?: Date
}

/** Whether the location's own hours admit `at`. */
function isWithinOperatingHours(
  { projectDateStore }: ILocationOpenContext,
  operatingIntervals: IMsDateInterval[],
  at: Date,
): boolean {
  return projectDateStore.isIntervalWithinOperatingWindows(
    at,
    new Date(at.getTime() + 1),
    buildEffectiveOperatingIntervalsFmt(projectDateStore, operatingIntervals),
  )
}

/**
 * Whether the location is actually usable at any moment of [start, end].
 *
 * The counterpart to isLocationClosedForEntirePeriod, which is what a range-aware
 * surface asks of closures: a period is not shut merely because part of it is.
 * Asked through findNextOpeningFmt, which already answers "when does this open
 * again" against the same windows and returns the current instant where the
 * location is open at `start`.
 *
 * Closures go in WITH the windows rather than being weighed separately. A
 * closure sitting exactly over the operating hours covers no whole calendar day,
 * so "not closed for the entire period" stays false while every usable minute is
 * gone - open 08:00-17:00, shut 08:00-17:00, and the badge said open. The
 * search subtracts them, so an opening it returns is one the site can be used
 * in.
 */
function opensWithinPeriod(
  { projectDateStore, closuresStore }: ILocationOpenContext,
  locationId: string,
  operatingIntervals: IMsDateInterval[],
  start: Date,
  end: Date,
): boolean {
  const closureIntervals = (closuresStore.getLocationClosures(locationId) || [])
    .map(closure => closure.closureInterval)
    .filter(Boolean)

  // Bounded by the period itself: the helper's own horizon is a giving-up point
  // for "when does this open again", and a selection can outrun it - the date
  // picker offers Three Weeks and Four Weeks, so a closure over the first
  // fortnight would have reported closed for a month the location mostly works.
  const opening = findNextOpeningFmt(
    projectDateStore,
    buildEffectiveOperatingIntervalsFmt(projectDateStore, operatingIntervals),
    new Date(start.getTime() - 1),
    closureIntervals,
    end,
  )

  // The loop steps whole days, so the last one can yield an opening past `end`.
  return !!opening && opening.startDate <= end.getTime()
}

/**
 * Whether the location reads as open on a card, a row or a badge.
 *
 * Where the surface shows a range and now falls outside it, the question becomes
 * the period's rather than this instant's - matching what the closure side has
 * always done, so the two halves of the answer speak about the same span.
 */
export function isLocationOpen(
  dataObject: ILocationOpenSource,
  context: ILocationOpenContext,
): boolean {
  const {
    projectDateStore,
    closuresStore,
    selectedStartDate,
    selectedEndDate,
  } = context

  if (!dataObject || !closuresStore || !projectDateStore) {
    return true
  }

  const operatingIntervals = dataObject.operatingIntervals || []
  const now = new Date()

  const isNowShown =
    !selectedStartDate ||
    projectDateStore.isWithinDateInterval(
      selectedStartDate,
      selectedEndDate,
      now,
    )

  if (isNowShown) {
    return (
      closuresStore.isLocationNowOpen(dataObject.id) &&
      isWithinOperatingHours(context, operatingIntervals, now)
    )
  }

  return (
    !closuresStore.isLocationClosedForEntirePeriod(
      dataObject.id,
      selectedStartDate,
      selectedEndDate,
    ) &&
    opensWithinPeriod(
      context,
      dataObject.id,
      operatingIntervals,
      selectedStartDate,
      selectedEndDate || selectedStartDate,
    )
  )
}
